import { useState } from "react";
import { Mail, MapPin, MessageCircle } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import {
    WHATSAPP_URL,
    CONTACT_EMAIL,
    GOOGLE_MAPS_EMBED,
    trackWhatsAppConversion,
} from "../data/mediaAssets";
import { useReveal } from "../hooks/useReveal";

const Contact = () => {
    const { t } = useI18n();
    const ref = useReveal();

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        trackWhatsAppConversion();
        const text =
            `Olá CH TOURS!%0A%0A` +
            `${t.contact.name}: ${form.name}%0A` +
            `${t.contact.email}: ${form.email}%0A` +
            `${t.contact.phone}: ${form.phone}%0A%0A` +
            `${t.contact.message}:%0A${form.message}`;
        window.open(`${WHATSAPP_URL}?text=${text}`, "_blank", "noopener");
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="bg-brand-cream py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center mb-14 md:mb-16 max-w-3xl mx-auto">
                    <p className="font-sans text-brand-gold text-xs uppercase tracking-[0.5em] mb-5">
                        — {t.contact.eyebrow}
                    </p>
                    <h2
                        data-testid="contact-title"
                        className="font-serif text-brand-navy text-4xl md:text-5xl lg:text-6xl leading-tight"
                    >
                        {t.contact.title}
                    </h2>
                    <p className="mt-6 font-sans text-neutral-600">{t.contact.intro}</p>
                </div>

                {/* Big WhatsApp CTA */}
                <div className="mb-14 flex justify-center">
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={trackWhatsAppConversion}
                        data-testid="contact-big-whatsapp"
                        className="group inline-flex items-center gap-4 bg-[#25D366] hover:bg-[#1fb454] text-white px-8 md:px-12 py-5 md:py-6 font-sans text-sm md:text-base uppercase tracking-[0.25em] transition-all hover:shadow-[0_20px_50px_rgba(37,211,102,0.35)]"
                    >
                        <MessageCircle className="w-6 h-6" />
                        <span>{t.contact.whatsapp_big}</span>
                    </a>
                </div>

                <div ref={ref} className="reveal grid lg:grid-cols-2 gap-10 lg:gap-16">
                    {/* Form */}
                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="bg-white border border-neutral-200 p-8 md:p-10 shadow-[0_20px_50px_-20px_rgba(11,27,61,0.15)]"
                    >
                        <div className="space-y-6">
                            <div>
                                <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                    {t.contact.name}
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    value={form.name}
                                    onChange={onChange}
                                    data-testid="contact-input-name"
                                    className="mt-2 w-full border-b border-neutral-300 bg-transparent py-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy"
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                        {t.contact.email}
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={form.email}
                                        onChange={onChange}
                                        data-testid="contact-input-email"
                                        className="mt-2 w-full border-b border-neutral-300 bg-transparent py-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy"
                                    />
                                </div>
                                <div>
                                    <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                        {t.contact.phone}
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={onChange}
                                        data-testid="contact-input-phone"
                                        className="mt-2 w-full border-b border-neutral-300 bg-transparent py-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                    {t.contact.message}
                                </label>
                                <textarea
                                    name="message"
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={onChange}
                                    data-testid="contact-input-message"
                                    className="mt-2 w-full border-b border-neutral-300 bg-transparent py-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy resize-none"
                                />
                            </div>

                            <p className="font-sans text-xs text-neutral-500">
                                {t.contact.form_hint}
                            </p>

                            <button
                                type="submit"
                                data-testid="contact-submit"
                                className="w-full bg-brand-navy hover:bg-brand-navy-hover text-white py-4 font-sans text-xs uppercase tracking-[0.3em] transition-colors"
                            >
                                {t.contact.send}
                            </button>
                        </div>
                    </form>

                    {/* Info + Map */}
                    <div className="flex flex-col gap-8">
                        <div className="bg-brand-navy text-white p-8 md:p-10">
                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <span className="p-2 border border-brand-gold/50">
                                        <Mail className="w-4 h-4 text-brand-gold" />
                                    </span>
                                    <div>
                                        <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-white/60">
                                            {t.contact.email_label}
                                        </p>
                                        <a
                                            href={`mailto:${CONTACT_EMAIL}`}
                                            data-testid="contact-email-link"
                                            className="font-serif text-lg md:text-xl hover:text-brand-gold transition-colors break-all"
                                        >
                                            {CONTACT_EMAIL}
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <span className="p-2 border border-brand-gold/50">
                                        <MapPin className="w-4 h-4 text-brand-gold" />
                                    </span>
                                    <div>
                                        <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-white/60">
                                            {t.contact.address_label}
                                        </p>
                                        <p
                                            className="font-serif text-lg md:text-xl"
                                            data-testid="contact-address"
                                        >
                                            {t.contact.address_value}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <span className="p-2 border border-brand-gold/50">
                                        <MessageCircle className="w-4 h-4 text-brand-gold" />
                                    </span>
                                    <div>
                                        <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-white/60">
                                            WhatsApp
                                        </p>
                                        <a
                                            href={WHATSAPP_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={trackWhatsAppConversion}
                                            data-testid="contact-whatsapp-link"
                                            className="font-serif text-lg md:text-xl hover:text-brand-gold transition-colors"
                                        >
                                            +351 931 880 078
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative overflow-hidden border border-neutral-200 h-72">
                            <iframe
                                title="CH TOURS location"
                                src={GOOGLE_MAPS_EMBED}
                                width="100%"
                                height="100%"
                                style={{ border: 0, filter: "grayscale(30%) contrast(1.05)" }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="contact-map"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
